import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { formatPrice } from '../data/products';
import { colorSwatch, productColors, productSizes } from '../components/ProductCard/ProductCard';
import { useCatalog } from '../data/useCatalog';
import './Product.css';

const ROTATE_MS = 2800;

function Product({ onAddToCart }) {
  const { id } = useParams();
  const { products, ready } = useCatalog();
  const [searchParams] = useSearchParams();
  const product = products.find((item) => String(item.id) === String(id));
  const initialColor = searchParams.get('color') || '';

  const colors = productColors(product ?? {});
  const sizes = productSizes(product ?? {});
  const colorSignature = colors.map((variant) => variant.color).join(',');

  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [qty, setQty] = useState(1);
  const [shot, setShot] = useState('');
  const strip = useRef(null);

  const activeVariant = colors.find((variant) => variant.color === selectedColor);
  const colorImage = activeVariant?.image || product?.image || '';
  const colorLabel = activeVariant?.name || selectedColor;
  const gallery = Array.isArray(product?.gallery) ? product.gallery.filter(Boolean) : [];
  const shots = [...new Set([colorImage, ...gallery].filter(Boolean))];
  const shotsKey = shots.join('\n');
  const shown = shot && shots.includes(shot) ? shot : colorImage;

  useEffect(() => {
    setSelectedColor(colorSignature.split(',').includes(initialColor) ? initialColor : '');
    setSelectedSize('');
    setQty(1);
    setShot('');
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id, initialColor, colorSignature]);

  useEffect(() => {
    const list = shotsKey.split('\n').filter(Boolean);
    if (list.length < 2) return undefined;
    const timer = setTimeout(() => setShot(list[(list.indexOf(shown) + 1) % list.length]), ROTATE_MS);
    return () => clearTimeout(timer);
  }, [shotsKey, shown]);

  useEffect(() => {
    const node = strip.current;
    const active = node?.querySelector('.product__shot.is-active');
    if (!node || !active) return;
    node.scrollTo({ left: Math.max(0, active.offsetLeft - (node.clientWidth - active.offsetWidth) / 2), behavior: 'smooth' });
  }, [shotsKey, shown]);

  if (!ready) return null;

  if (!product) {
    return (
      <main className="product">
        <div className="product__missing">
          <h1 className="product__title">This product is not in the lineup.</h1>
          <Link className="btn" to="/">Back to the store</Link>
        </div>
      </main>
    );
  }

  const needsSize = sizes.length > 0 && !selectedSize;
  const addDisabled = Boolean(product.outOfStock) || needsSize;

  function addToCart() {
    onAddToCart?.(product, selectedColor || selectedSize
      ? {
          ...(selectedColor ? { color: selectedColor, name: colorLabel || selectedColor, image: activeVariant?.image || product.image || '' } : {}),
          ...(selectedSize ? { size: selectedSize } : {}),
        }
      : null);
  }

  return (
    <main className="product">
      <div className="product__layout">
        <div className="product__media">
          <div className="product__stage">
            {shown ? (
              <img className="product__img" src={shown} alt={colorLabel ? `${product.name} — ${colorLabel}` : product.name} />
            ) : (
              <span className="product__photo-note">Photo coming soon</span>
            )}
          </div>

          {shots.length > 1 && (
            <div className="product__shots" ref={strip}>
              {shots.map((url) => (
                <button
                  type="button"
                  key={url}
                  className={`product__shot${shown === url ? ' is-active' : ''}`}
                  onClick={() => setShot(url)}
                  aria-label={`Show photo of ${product.name}`}
                >
                  <img src={url} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="product__info">
          <Link className="product__back" to="/">Back to the store</Link>
          <h1 className="product__title">{product.name}</h1>

          <p className="product__price">
            <span className="product__price-now">{formatPrice(product.price)}</span>
            {product.oldPrice && <span className="product__price-was">{formatPrice(product.oldPrice)}</span>}
            {product.outOfStock && <span className="product__sold">Sold out</span>}
          </p>

          {colors.length > 0 && (
            <div className="product__option">
              <span className="product__option-label">Color{colorLabel ? ` · ${colorLabel}` : ''}</span>
              <div className="product__colors" role="group" aria-label="Available colors">
                {colors.map((variant) => {
                  const swatch = colorSwatch(variant.color);
                  const label = variant.name || swatch.label;
                  return (
                    <button
                      type="button"
                      key={variant.color}
                      className={`product__swatch${selectedColor === variant.color ? ' is-selected' : ''}`}
                      style={{ backgroundColor: swatch.value }}
                      title={label}
                      aria-label={label}
                      aria-pressed={selectedColor === variant.color}
                      onClick={() => {
                        setSelectedColor((current) => (current === variant.color ? '' : variant.color));
                        setShot('');
                      }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {sizes.length > 0 && (
            <div className="product__option">
              <span className="product__option-label">Size</span>
              <div className="product__sizes" role="group" aria-label="Available sizes">
                {sizes.map((size) => (
                  <button
                    type="button"
                    key={size}
                    className={`product__size${selectedSize === size ? ' is-selected' : ''}`}
                    aria-pressed={selectedSize === size}
                    onClick={() => setSelectedSize((current) => (current === size ? '' : size))}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="product__option">
            <span className="product__option-label">Quantity</span>
            <div className="product__qty">
              <button type="button" className="product__step" aria-label="One less" disabled={qty <= 1} onClick={() => setQty((value) => Math.max(1, value - 1))}>
                &minus;
              </button>
              <span className="product__count">{qty}</span>
              <button type="button" className="product__step" aria-label="One more" onClick={() => setQty((value) => value + 1)}>
                +
              </button>
            </div>
          </div>

          <button type="button" className="product__add" disabled={addDisabled} onClick={addToCart}>
            {product.outOfStock ? 'Sold out' : needsSize ? 'Choose a size' : `Add to cart — ${formatPrice(product.price * qty)}`}
          </button>
        </div>
      </div>
    </main>
  );
}

export default Product;
